import { Modal, Form, Input, Select, notification, Switch } from "antd";
import {
  MachineItem,
  ProductCardItem,
  useUpdateTaskMutation,
  useGetTaskByIdQuery,
  useGetAllMachinesQuery,
  useGetAllProductCardsQuery,
  useUploadImageMutation,
  useDeleteTaskFileMutation,
  useUploadFileMutation,
} from "shared/api";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import {
  TaskIconUploader,
  PeriodicitySection,
  AssociationsSection,
} from "./components";

dayjs.extend(utc);
dayjs.extend(timezone);

interface EditTaskModalProps {
  open: boolean;
  onClose: () => void;
  taskId: number | null;
  refetch: () => void;
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  refetchExecutions: () => void | undefined;
  suppliersCompaniesFormatted:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

export const EditTaskModal: React.FC<EditTaskModalProps> = ({
  open,
  onClose,
  taskId,
  refetch,
  transformedUsers,
  refetchExecutions,
  suppliersCompaniesFormatted,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const [fileName, setFileName] = useState<string | null>(null);
  const fileDocRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedIconFile, setUploadedIconFile] = useState<File | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [repeatEvery, setRepeatEvery] = useState<string | null>(null);

  const periodicity = Form.useWatch("periodicity", form);
  const isRecurrent = Form.useWatch("is_recurrent", form);
  const selectedTime = Form.useWatch("time", form);
  const enabled = Form.useWatch("enabled", form);

  useEffect(() => {
    setRepeatEvery(periodicity || null);
  }, [periodicity]);

  const params =
    open && taskId != null
      ? { id: taskId, expand: "machines,product_cards,files,users,companies" }
      : skipToken;

  const { data: taskData, refetch: refetchTaskById } =
    useGetTaskByIdQuery(params);

  const { data: machinesData } = useGetAllMachinesQuery({}) as {
    data: MachineItem[] | undefined;
  };
  const { data: productCardsData } = useGetAllProductCardsQuery({}) as {
    data: ProductCardItem[] | undefined;
  };

  useEffect(() => {
    if (open && taskId) {
      refetchTaskById();
    }
  }, [open, taskId]);

  const attachedFiles = taskData?.files ?? [];
  const [uploadImage] = useUploadImageMutation();
  const [updateTask, { isLoading }] = useUpdateTaskMutation();
  const [deleteFile] = useDeleteTaskFileMutation();
  const [uploadFile] = useUploadFileMutation();

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    setNewFiles((prev) => [...prev, ...Array.from(files)]);
  };

  useEffect(() => {
    if (open && taskData) {
      const timezone = localStorage.getItem("timezone") || "UTC";

      form.setFieldsValue({
        name: taskData.name,
        periodicity: taskData.periodicity,
        periodicity_multiple: taskData.periodicity_multiple ?? null,
        is_recurrent: taskData.is_recurrent || false,
        time: taskData.time ? dayjs(taskData.time, "HH:mm:ss") : null,
        start_periodicity: taskData.start_periodicity
          ? dayjs.utc(taskData.start_periodicity).tz(timezone)
          : null,
        end_periodicity: taskData.end_periodicity
          ? dayjs.utc(taskData.end_periodicity).tz(timezone)
          : null,
        machines: taskData.machines?.map((m) => m.machine.id) || [],
        product_cards:
          taskData.product_cards?.map((p) => p.product_card.id) || [],
        icon: taskData.icon,
        enabled: taskData.enabled,
        priority: taskData.priority,
        users: taskData.users?.map((u) => u.user.id) || [],
        companies: taskData.companies?.map((c) => c.company.id) || [],
        description: taskData.description ?? "",
      });

      if (taskData.description) setShowDescription(true);
      if (taskData.icon) {
        setPreviewUrl(taskData.icon);
        setFileName(taskData.icon.split("/").pop() ?? "icon.png");
      } else {
        setFileName(null);
      }
    }

    if (!open) {
      form.resetFields();
      setFileName(null);
      setPreviewUrl(null);
      setUploadedIconFile(null);
      setNewFiles([]);
      setShowDescription(false);
    }
  }, [open, taskData, form]);

  const handleFinish = async (values: any) => {
    try {
      const payload = {
        name: values.name,
        periodicity: values.periodicity || null,
        periodicity_multiple: values.periodicity_multiple ?? null,
        is_recurrent: values.is_recurrent || false,
        time: values.time ? values.time.format("HH:mm:ss") : null,
        start_periodicity: values.start_periodicity
          ? dayjs(values.start_periodicity)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        end_periodicity: values.end_periodicity
          ? dayjs(values.end_periodicity).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        machines: values.machines || [],
        product_cards: values.product_cards || [],
        enabled: values.enabled,
        priority: values.priority,
        users: values.users || [],
        description: showDescription ? values.description : null,
        companies: values.companies ?? [],
      };

      const task = await updateTask({ id: taskId, ...payload }).unwrap();

      if (task && uploadedIconFile) {
        await uploadImage({ id: task.id, file: uploadedIconFile }).unwrap();
      }

      if (newFiles.length > 0 && taskId) {
        await Promise.all(
          newFiles.map((file) => uploadFile({ id: taskId, file }).unwrap())
        );
      }

      refetchExecutions();
      api.success({
        message: "Task Updated",
        description: "The task was successfully updated.",
        placement: "topRight",
      });

      refetch();
      onClose();
    } catch (err) {
      api.error({
        message: "Update Failed",
        description: "An error occurred while updating the task.",
        placement: "topRight",
      });
      // eslint-disable-next-line no-console
      console.error("Failed to update task", err);
    } finally {
      onClose();
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    if (!taskId) return;
    try {
      await deleteFile({ taskId, fileId }).unwrap();
      refetchTaskById();
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to delete file", error);
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleIconUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setPreviewUrl(base64);
      setUploadedIconFile(file);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleIconRemove = () => {
    setPreviewUrl(null);
    setUploadedIconFile(null);
    setFileName(null);
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        destroyOnClose
        title={
          <div className="flex items-center justify-between w-[95%] mb-3">
            <span>{t("taskHub.modal.editTask") || ""}</span>
            <div className="flex gap-2 items-center min-w-[130px] justify-end">
              <span className="text-sm">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onChange={(checked) => form.setFieldValue("enabled", checked)}
                style={{ backgroundColor: enabled ? "limegreen" : "tomato" }}
              />
            </div>
          </div>
        }
        width={720}
        okButtonProps={{
          style: {
            backgroundColor: "#1677ff",
            color: "#fff",
            borderColor: "#1677ff",
          },
        }}
        okText={t("taskHub.modal.ok") || ""}
        cancelText={t("taskHub.modal.cancel") || ""}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <div>
            <div className="grid grid-cols-2 gap-x-4">
              <div className="col-span-2 flex items-center justify-between">
                <h3 className="text-base font-semibold mb-2">
                  {t("taskHub.modal.taskIcon") || ""}
                </h3>
                <TaskIconUploader
                  previewUrl={previewUrl}
                  fileName={fileName}
                  uploadedIconFile={uploadedIconFile}
                  onUpload={handleIconUpload}
                  onRemove={handleIconRemove}
                />
              </div>

              <Form.Item
                name="name"
                label={t("taskHub.modal.name") || ""}
                rules={[{ required: true }]}
              >
                <Input placeholder={t("taskHub.modal.enterTaskName") || ""} />
              </Form.Item>

              <Form.Item name="users" label={t("taskHub.modal.users") || ""}>
                <Select
                  mode="multiple"
                  showSearch
                  options={transformedUsers}
                  optionFilterProp="label"
                  placeholder={t("taskHub.modal.selectUsers") || ""}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="priority"
                label={t("taskHub.modal.priority") || ""}
              >
                <Select placeholder={t("taskHub.modal.selectPriority") || ""}>
                  <Select.Option value={1}>1</Select.Option>
                  <Select.Option value={2}>2</Select.Option>
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={4}>4</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </div>

          <PeriodicitySection
            isRecurrent={isRecurrent}
            repeatEvery={repeatEvery}
            selectedTime={selectedTime}
            form={form}
          />

          <AssociationsSection
            machinesData={machinesData}
            productCardsData={productCardsData}
            suppliersCompaniesFormatted={suppliersCompaniesFormatted}
            showDescription={showDescription}
            attachedFiles={attachedFiles}
            newFiles={newFiles}
            themeColor={themeColor}
            fileDocRef={fileDocRef}
            onToggleDescription={() => setShowDescription((prev) => !prev)}
            onAttachFiles={handleAttachFiles}
            onDeleteFile={handleDeleteFile}
            onRemoveNewFile={handleRemoveNewFile}
          />

          <Form.Item name="enabled" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
